/**
 * Setup Fee quantity sync
 *
 * Keeps the Setup Fee line item's quantity from exceeding the total custom-wick
 * quantity in the cart (any variant / pack size), using the Cart AJAX API:
 *
 *   Setup Fee (variant ID 35935883853978 / product ID 9267498090742)
 *     - allowed = total quantity of custom-wicks lines, regardless of variant
 *     - removed entirely when no custom-wicks lines are in the cart
 *
 * If its total quantity is within the allowed amount nothing happens;
 * otherwise it is trimmed down to the allowed amount (a cap of 0 removes the
 * Setup Fee lines).
 *
 * To keep the quantity and price updates smooth (no "added then removed"
 * flicker), cart mutations are intercepted: when a change/add would push the
 * Setup Fee over its allowance, the clamp is applied *before* the corrected
 * cart is handed back to the theme, so the theme renders the final state in a
 * single pass. The affected quantity inputs are also snapped immediately so
 * the number does not wait on the theme's delayed re-render.
 */
(function () {
  'use strict';

  const SETUP_FEE_VARIANT_ID = 35935883853978;
  const SETUP_FEE_PRODUCT_ID = 9267498090742;

  const CUSTOM_WICKS_PRODUCT_TYPE = 'custom-wicks';

  const originalFetch = window.fetch.bind(window);
  /* Guards our own correction requests from being intercepted again */
  let bypass = false;

  /* ----------------------------------------------------------------- *
   * Cart item classification
   * ----------------------------------------------------------------- */

  function isSetupFeeItem(item) {
    /* cart.js lines expose variant_id; add.js lines expose it as id */
    const variantId = item.variant_id != null ? item.variant_id : item.id;
    return variantId === SETUP_FEE_VARIANT_ID || item.product_id === SETUP_FEE_PRODUCT_ID;
  }

  function isWickItem(item) {
    return item.product_type === CUSTOM_WICKS_PRODUCT_TYPE &&
      !isSetupFeeItem(item);
  }

  function sumQuantity(items) {
    return items.reduce(function (total, item) { return total + item.quantity; }, 0);
  }

  /**
   * Returns the /cart/update.js "updates" object trimming the Setup Fee lines
   * down to the total custom-wicks quantity (any variant/pack size), or an
   * empty object when nothing exceeds it. Earlier lines keep their quantity
   * first; the excess is removed from later lines (a cap of 0 removes the line).
   */
  function computeUpdates(cart) {
    if (!cart || !Array.isArray(cart.items)) return {};

    const setupFeeLines = cart.items.filter(isSetupFeeItem);
    if (setupFeeLines.length === 0) return {};

    /* Setup fee quantity may not exceed the total custom-wicks quantity,
     * regardless of variant/pack size. */
    const wicksQuantity = sumQuantity(cart.items.filter(isWickItem));

    const updates = {};
    if (sumQuantity(setupFeeLines) > wicksQuantity) {
      let remaining = wicksQuantity;
      setupFeeLines.forEach(function (line) {
        const keepQuantity = Math.min(line.quantity, remaining);
        remaining -= keepQuantity;
        if (keepQuantity !== line.quantity) {
          updates[line.key] = keepQuantity;
        }
      });
    }
    return updates;
  }

  /* ----------------------------------------------------------------- *
   * Helpers
   * ----------------------------------------------------------------- */

  function isCartPage() {
    return window.themeVariables?.settings?.pageType === 'cart';
  }

  function cartDrawerSectionId() {
    const drawer = document.querySelector('#cart-drawer');
    if (!drawer) return null;
    const wrapper = drawer.closest('.shopify-section') || drawer.parentNode;
    return wrapper && wrapper.id ? wrapper.id.replace('shopify-section-', '') : null;
  }

  function requestUrl(input) {
    return typeof input === 'string' ? input : (input && input.url) || '';
  }

  function requestMethod(input, init) {
    return ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
  }

  /* Parses the JSON request body, so we can preserve the sections the caller
   * asked for when we re-issue the corrected update. */
  function readRequestBody(input, init) {
    try {
      const body = (init && init.body) || (input && input.body);
      if (typeof body === 'string') return JSON.parse(body);
    } catch (e) { /* not JSON */ }
    return null;
  }

  /**
   * Issues the clamp via /cart/update.js (bypassing interception) and returns
   * the corrected cart, or null when nothing needed clamping.
   */
  async function clampCart(cart, requestedSections) {
    const updates = computeUpdates(cart);
    if (Object.keys(updates).length === 0) return null;

    const body = { updates: updates };
    if (requestedSections) body.sections = requestedSections;

    bypass = true;
    try {
      const response = await originalFetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const correctedCart = await response.json();
      return { cart: correctedCart, updates: updates };
    } finally {
      bypass = false;
    }
  }

  /* Snaps the affected line quantity inputs (and hides removed lines) right
   * away, so the number does not visibly wait on the theme's delayed render. */
  function syncQuantityInputs(updates) {
    Object.keys(updates).forEach(function (key) {
      const input = document.querySelector('.quantity-input[data-line-key="' + key + '"]');
      if (!input) return;
      const quantity = updates[key];
      if (quantity > 0) {
        input.value = String(quantity);
      } else {
        const lineItem = input.closest('line-item');
        if (lineItem) lineItem.style.display = 'none';
      }
    });
  }

  /**
   * Immediately renders the corrected line items into the open cart drawer,
   * so the quantity AND line prices update together without waiting on the
   * theme's delayed (1250ms) line-items re-render. The theme's footer/total
   * update is already immediate, so this keeps everything in sync. Returns
   * true when the swap happened.
   */
  function renderCorrectedLineItems(sectionHtml) {
    const container = document.querySelector('.cart-drawer__line-items');
    if (!container || !sectionHtml) return false;
    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const updated = parsed.querySelector('.cart-drawer__line-items');
    if (!updated) return false;
    container.innerHTML = updated.innerHTML;
    return true;
  }

  function jsonResponse(data) {
    return new Response(JSON.stringify(data), {
      status: 200,
      statusText: 'OK',
      headers: { 'Content-Type': 'application/json' }
    });
  }

  /* ----------------------------------------------------------------- *
   * Fetch interception
   * ----------------------------------------------------------------- */

  window.fetch = async function (input, init) {
    const response = await originalFetch(input, init);
    if (bypass) return response;

    try {
      if (requestMethod(input, init) !== 'POST') return response;

      const url = requestUrl(input);
      const isChange = /\/cart\/(change|update)\.js(\?|$)/.test(url);
      const isAdd = /\/cart\/add\.js(\?|$)/.test(url);
      if (!isChange && !isAdd) return response;

      if (isChange) {
        /* The response body is the full cart: clamp it before the theme sees
         * it, and hand the corrected cart back so it renders once. */
        const cart = await response.clone().json();
        const sectionId = cartDrawerSectionId();
        const requestBody = readRequestBody(input, init);
        const requestedSections = (requestBody && requestBody.sections) ||
          (sectionId ? [sectionId] : null);
        const clamped = await clampCart(cart, requestedSections);
        if (!clamped) return response;

        /* Render the corrected line items now (quantity + line prices) so they
         * land together with the theme's immediate footer/total update,
         * instead of lagging behind it by the theme's 1250ms delay. */
        const rendered = sectionId && clamped.cart.sections &&
          renderCorrectedLineItems(clamped.cart.sections[sectionId]);
        if (!rendered) syncQuantityInputs(clamped.updates);

        return jsonResponse(clamped.cart);
      }

      /* Add: the response is the added line item(s), not the cart. Only the
       * setup fee can push its group over its allowance, so skip the extra
       * work for any other add. */
      const added = await response.clone().json();
      const addedItems = Array.isArray(added.items) ? added.items : [added];
      const touchesDependent = addedItems.some(function (item) {
        return item && isSetupFeeItem(item);
      });
      if (!touchesDependent) return response;

      /* Clamp the cart server-side now. */
      bypass = true;
      let cart;
      try {
        cart = await (await originalFetch(`${Shopify.routes.root}cart.js`)).json();
      } finally {
        bypass = false;
      }

      /* Re-render the cart drawer section against the corrected cart so the
       * removed/reduced item is gone, then splice it into the add response.
       * The theme renders the drawer from this response's bundled sections
       * (cartContent.sections = json.sections), so without this the removed
       * item would still appear until the next cart event. */
      const sectionId = cartDrawerSectionId();
      const requestBody = readRequestBody(input, init);
      const requestedSections = (requestBody && requestBody.sections) ||
        (sectionId ? [sectionId] : null);
      const clamped = await clampCart(cart, requestedSections);
      if (!clamped) return response;

      syncQuantityInputs(clamped.updates);

      if (added.sections && clamped.cart.sections && sectionId &&
          clamped.cart.sections[sectionId]) {
        added.sections[sectionId] = clamped.cart.sections[sectionId];
        return jsonResponse(added);
      }

      /* Fallback: couldn't patch the bundled section, so force a clean
       * server re-render to drop the ineligible item from the view. */
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return response;
    } catch (error) {
      console.error('setup-fee-cart-sync:', error);
      return response;
    }
  };

  /* ----------------------------------------------------------------- *
   * Initial load: correct a cart that already violates the rules
   * ----------------------------------------------------------------- */

  async function correctOnLoad() {
    try {
      bypass = true;
      let cart;
      try {
        cart = await (await originalFetch(`${Shopify.routes.root}cart.js`)).json();
      } finally {
        bypass = false;
      }
      const clamped = await clampCart(cart);
      if (clamped) {
        if (isCartPage()) {
          window.location.reload();
        } else {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      }
    } catch (error) {
      console.error('setup-fee-cart-sync:', error);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', correctOnLoad);
  } else {
    correctOnLoad();
  }
})();
